import { useEffect, useRef, type KeyboardEvent } from 'react'
import { BookPlus, CornerDownLeft, Pencil } from 'lucide-react'
import { cn } from '@/lib/utils'
import { PromptCounter } from '@/features/ai/components/PromptCounter'
import type { SidebarMode } from './types'
import { useMessages } from '@/i18n'
import { messages } from './sidebarMessages'

interface Props {
  value: string
  onChange: (v: string) => void
  mode: SidebarMode
  onToggleMode: (m: Exclude<SidebarMode, 'idle'>) => void
  placeholder: string
  isDisabled: boolean
  /**
   * Идёт прогон — пилюли режимов гаснут обе (R3, как в рефе). Отдельно от
   * `isDisabled`: тот истинен и в `idle`, где переключать режим как раз нужно.
   */
  isRunning: boolean
  /**
   * Отправка вообще возможна — считает РОДИТЕЛЬ (I-1). Своей формулы у
   * композера нет намеренно: она не знала бы про лимит промпта, и кнопка
   * оставалась бы синей на 2500 символах, а клик молча возвращал `false`.
   * Тот же флаг кормит «Повторить» в баннере отказа — источник один.
   */
  canSubmit: boolean
  /** Потолок счётчика — свой у каждого режима (правка блока ограничена
   *  `BLOCK_EDIT_PROMPT_MAX`, не `MAX_PROMPT_LENGTH`); считает РОДИТЕЛЬ. */
  promptMax: number
  onSubmit: () => void
}

const modesOf = (m: {
  modeGenerate: string
  modeEditBlock: string
}): { id: Exclude<SidebarMode, 'idle'>; label: string; icon: typeof BookPlus }[] => [
  { id: 'generate', label: m.modeGenerate, icon: BookPlus },
  { id: 'edit-block', label: m.modeEditBlock, icon: Pencil },
]

const modePillClass = (id: Exclude<SidebarMode, 'idle'>, mode: SidebarMode) => {
  if (mode !== id) return 'text-muted-foreground hover:bg-foreground/5 hover:text-foreground'
  return id === 'generate'
    ? 'bg-[hsl(var(--mode-generate-bg))] text-[hsl(var(--mode-generate-fg))]'
    : 'bg-[hsl(var(--mode-edit-bg))] text-[hsl(var(--mode-edit-fg))]'
}

/**
 * Кнопки «Остановить» здесь нет: у прогона нет ручки отмены (контракт §8.4,
 * пункт 5 — «кнопка ничего не делает»). Пока прогон идёт (или пока режим не
 * `generate`, у которого одного есть реальный `send`), поле просто гасится
 * через `isDisabled` — честнее нерабочей кнопки.
 */
export const AIComposer = ({
  value,
  onChange,
  mode,
  onToggleMode,
  placeholder,
  isDisabled,
  isRunning,
  canSubmit,
  promptMax,
  onSubmit,
}: Props) => {
  const m = useMessages(messages)
  const fieldRef = useRef<HTMLTextAreaElement>(null)

  // Отступление от рефа (там однострочный input): растим до 120px и оставляем
  // Shift+Enter — промпты бывают многострочными. Высота — функция от value (не
  // от истории нажатий): value очищается/подставляется родителем («Попробовать
  // снова»), и это не keystroke, поэтому onInput тут недостаточно.
  useEffect(() => {
    const el = fieldRef.current
    if (!el) return
    el.style.height = 'auto'
    el.style.height = `${Math.min(el.scrollHeight, 120)}px`
  }, [value])

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key !== 'Enter' || e.shiftKey || !canSubmit) return
    e.preventDefault()
    onSubmit()
  }

  return (
    <div className="p-3 pt-1.5">
      <div className="rounded-2xl border border-border bg-card">
        <textarea
          ref={fieldRef}
          value={value}
          rows={1}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          disabled={isDisabled}
          className="w-full resize-none bg-transparent px-4 py-3 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none disabled:opacity-50"
        />
        <PromptCounter length={value.trim().length} className="px-3.5 pb-1" max={promptMax} />
        <div className="flex items-center justify-between px-2.5 pb-2.5 pt-1">
          <div className="flex items-center gap-1">
            {modesOf(m).map(({ id, label, icon: Icon }) => (
              <button
                key={id}
                type="button"
                onClick={() => onToggleMode(id)}
                // Пока прогон идёт, режим не переключается: поле всё равно
                // погашено, а активный тон живой пилюли этому противоречил.
                disabled={isRunning}
                className={cn(
                  'flex items-center gap-1 rounded-lg px-2.5 py-1 text-xs font-medium transition-all',
                  modePillClass(id, mode),
                  isRunning ? 'cursor-not-allowed opacity-50' : null,
                )}
              >
                <Icon className="h-3 w-3" />
                {label}
              </button>
            ))}
          </div>
          <button
            type="button"
            onClick={onSubmit}
            disabled={!canSubmit}
            aria-label={m.send}
            className="rounded-lg p-1.5 text-muted-foreground transition-colors hover:bg-foreground/5 hover:text-foreground disabled:opacity-30"
          >
            <CornerDownLeft className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  )
}
